/**
 * PrintWorx3D Rewards — storefront client for the "My Rewards" page.
 *
 * Talks only to the Shopify App Proxy (/apps/rewards/*). Sends a reward id and a request id when the
 * customer redeems; never a customer id, a points amount or a discount value. Every number shown comes
 * from the service (spendable and pending are separate fields and are never added together here).
 * Every request has a timeout and every failure degrades to a quiet note — nothing here touches the
 * cart or checkout.
 */
const root = document.querySelector("[data-pw-rewards]");

if (root) {
  const endpoint = (root.dataset.endpoint || "/apps/rewards").replace(/\/+$/, "");
  const token = root.dataset.token || "";
  const loginUrl = root.dataset.loginUrl || "/account/login";
  const shopUrl = root.dataset.shopUrl || "/collections/all";
  const themeSaysLoggedIn = root.dataset.loggedIn === "true";
  const READ_TIMEOUT_MS = 6000;
  /** A redeem creates the code at Shopify before answering; give it time rather than inviting a second click. */
  const REDEEM_TIMEOUT_MS = 15000;

  const states = Object.fromEntries([...root.querySelectorAll("[data-state]")].map((el) => [el.dataset.state, el]));
  const tpl = (name) => root.querySelector(`template[data-tpl="${name}"]`);
  const $ = (sel, scope = root) => scope.querySelector(sel);
  const fmtPoints = (n) => Number(n || 0).toLocaleString("en-ZA");
  const fmtRand = (n) => `R${Number(n).toLocaleString("en-ZA", { maximumFractionDigits: Number(n) % 1 === 0 ? 0 : 2 })}`;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : "");
  const valueLabel = (r) => (r.discountType === "percentage" ? `${r.discountValue}% off` : `${fmtRand(r.discountValue)} off`);
  const applyUrl = (code) => `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(shopUrl)}`;

  function show(state) {
    for (const [name, el] of Object.entries(states)) el.hidden = name !== state;
  }

  async function api(path, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs || READ_TIMEOUT_MS);
    try {
      const headers = { accept: "application/json" };
      if (options.body) headers["content-type"] = "application/json";
      if (token) headers["x-pw-rewards-token"] = token;
      const res = await fetch(`${endpoint}${path}`, { method: options.method || "GET", headers, body: options.body ? JSON.stringify(options.body) : undefined, credentials: "same-origin", signal: controller.signal });
      const data = await res.json().catch(() => null);
      return { status: res.status, data };
    } catch {
      return { status: 0, data: null };
    } finally {
      clearTimeout(timer);
    }
  }

  /** Copies text and confirms on the button that was pressed (captured before the await; the event is gone by then). */
  async function copyWithFeedback(button, text, fallback) {
    const label = button.dataset.label || (button.dataset.label = button.textContent);
    try {
      await navigator.clipboard.writeText(text);
      button.textContent = "Copied";
      button.classList.add("is-copied");
      setTimeout(() => { button.textContent = label; button.classList.remove("is-copied"); }, 2000);
    } catch {
      if (fallback) fallback();
    }
  }

  function fill(fragment, fields) {
    for (const [key, value] of Object.entries(fields)) {
      const el = fragment.querySelector(`[data-field="${key}"]`);
      if (el && value != null) el.textContent = String(value);
    }
    return fragment;
  }

  function renderEarn(list, actions) {
    list.replaceChildren();
    for (const a of actions || []) {
      const node = tpl("earn").content.cloneNode(true);
      fill(node, { name: a.name, desc: a.description });
      const tag = node.querySelector('[data-field="tag"]');
      if (a.status !== "active") { tag.textContent = a.status === "coming_soon" ? "Coming soon" : "Not available"; tag.hidden = false; }
      else if (a.points) { tag.textContent = `${fmtPoints(a.points)} points`; tag.hidden = false; }
      list.append(node);
    }
  }

  function renderRewards(list, rewards, mode) {
    list.replaceChildren();
    for (const r of rewards || []) {
      const node = tpl("reward").content.cloneNode(true);
      fill(node, { cost: fmtPoints(r.pointsCost), value: valueLabel(r), desc: r.description || "" });
      const li = node.querySelector("li");
      const btn = node.querySelector('[data-field="button"]');
      if (mode === "public") {
        btn.textContent = "Sign in to redeem";
        btn.classList.add("button-secondary");
        btn.addEventListener("click", () => { window.location.href = loginUrl; });
      } else if (r.affordable) {
        btn.addEventListener("click", () => openRedeem(r));
      } else {
        li.classList.add("pw-rewards__card--locked");
        btn.textContent = `${fmtPoints(r.pointsCost - currentSpendable)} more points`;
        btn.classList.add("button-secondary");
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
      }
      list.append(node);
    }
  }

  function renderBalance(container, me) {
    const node = tpl("balance").content.cloneNode(true);
    fill(node, { spendable: fmtPoints(me.points.spendable), pending: fmtPoints(me.points.pending), total: fmtPoints(me.points.total), earned: fmtPoints(me.points.lifetimeEarned), redeemed: fmtPoints(me.points.lifetimeRedeemed) });
    node.querySelector('[data-field="pending-wrap"]').hidden = !(me.points.pending > 0);
    if (me.nextReward) {
      const wrap = node.querySelector('[data-field="progress-wrap"]');
      const pct = Math.max(0, Math.min(100, Math.round((me.points.spendable / me.nextReward.pointsCost) * 100)));
      wrap.hidden = false;
      node.querySelector('[data-field="bar"]').setAttribute("aria-valuenow", String(pct));
      node.querySelector('[data-field="bar-fill"]').style.width = `${pct}%`;
      node.querySelector('[data-field="progress-text"]').textContent = `${fmtPoints(me.nextReward.pointsToGo)} more points until ${me.nextReward.name}`;
    } else if (me.rewards && me.rewards.length) {
      const wrap = node.querySelector('[data-field="progress-wrap"]');
      wrap.hidden = false;
      node.querySelector('[data-field="bar"]').setAttribute("aria-valuenow", "100");
      node.querySelector('[data-field="bar-fill"]').style.width = "100%";
      node.querySelector('[data-field="progress-text"]').textContent = "You can redeem every reward below.";
    }
    container.replaceChildren(node);
  }

  function row(title, sub, amount, minus) {
    const li = document.createElement("li");
    li.className = "pw-rewards__row";
    const t = document.createElement("p"); t.className = "pw-rewards__row-title"; t.textContent = title;
    const s = document.createElement("p"); s.className = "pw-rewards__row-sub"; s.textContent = sub;
    const a = document.createElement("p"); a.className = `pw-rewards__row-amount${minus ? " pw-rewards__row-amount--minus" : ""}`; a.textContent = amount;
    li.append(t, s, a);
    return li;
  }

  function renderRedemptions(list, empty, redemptions) {
    list.replaceChildren();
    empty.hidden = redemptions.length > 0;
    for (const r of redemptions) {
      const status = { issued: "Ready to use", used: "Used", expired: "Expired", pending_retry: "Being prepared", issuing: "Being prepared", cancelled: "Cancelled", failed: "Points returned" }[r.status] || r.status;
      const sub = `${status}${r.expiresAt && r.status === "issued" ? ` · valid until ${fmtDate(r.expiresAt)}` : ""} · ${fmtDate(r.createdAt)}`;
      const li = row(r.rewardName === r.valueLabel ? r.rewardName : `${r.rewardName} · ${r.valueLabel}`, sub, r.code || "—", false);
      if (r.code) {
        li.querySelector(".pw-rewards__row-amount").classList.add("pw-rewards__row-code");
        if (r.status === "issued") {
          const actions = document.createElement("p");
          actions.className = "pw-rewards__row-actions";
          const copy = document.createElement("button"); copy.type = "button"; copy.className = "pw-rewards__link"; copy.textContent = "Copy code";
          copy.addEventListener("click", () => copyWithFeedback(copy, r.code));
          const a = document.createElement("a"); a.href = applyUrl(r.code); a.textContent = "Apply and shop"; a.className = "pw-rewards__link";
          actions.append(copy, document.createTextNode(" · "), a);
          li.append(actions);
        }
      }
      list.append(li);
    }
  }

  function renderHistory(list, empty, history) {
    list.replaceChildren();
    empty.hidden = history.length > 0;
    for (const h of history) {
      const pendingNote = new Date(h.availableAt) > new Date() && h.points > 0 ? ` · spendable from ${fmtDate(h.availableAt)}` : "";
      list.append(row(h.label, `${h.reference ? `${h.reference} · ` : ""}${fmtDate(h.createdAt)}${pendingNote}`, `${h.points > 0 ? "+" : ""}${fmtPoints(h.points)}`, h.points < 0));
    }
  }

  function renderReferral(panel, ref) {
    if (!panel || !ref || !ref.code) { if (panel) panel.hidden = true; return; }
    panel.hidden = false;
    const points = ref.points != null ? `${fmtPoints(ref.points)} points` : "points";
    $("[data-refer-text]", panel).textContent = ref.enabled
      ? `When a friend places their first order through your link, you earn ${points} once it clears the returns window. Pending rewards become spendable automatically.`
      : "Your personal link is ready. Referral rewards will switch on soon.";
    $("[data-refer-code]", panel).textContent = ref.code;
    $("[data-refer-url]", panel).value = ref.url;
    $("[data-refer-successful]", panel).textContent = fmtPoints(ref.successful);
    $("[data-refer-pending]", panel).textContent = fmtPoints(ref.pendingPoints);
    $("[data-refer-earned]", panel).textContent = fmtPoints(ref.earnedPoints);
    $("[data-refer-off]", panel).hidden = ref.enabled;
    const share = $("[data-refer-share]", panel);
    if (share) {
      const canShare = typeof navigator.share === "function";
      share.hidden = !canShare;
      share.onclick = () => navigator.share({ title: "PrintWorx3D", text: `Get money off your first PrintWorx3D order with my link: ${ref.url}`, url: ref.url }).catch(() => {});
    }
    const list = $("[data-refer-history]", panel);
    const empty = $("[data-refer-empty]", panel);
    list.replaceChildren();
    const history = ref.history || [];
    if (empty) empty.hidden = history.length > 0;
    for (const h of history) {
      const label = { pending: "Pending", earned: "Earned", reversed: "Reversed" }[h.status] || h.status;
      const sub = h.status === "pending" && h.maturesAt ? `A friend's first order · spendable from ${fmtDate(h.maturesAt)}` : `A friend's first order · ${fmtDate(h.createdAt)}`;
      list.append(row(label, sub, `${h.status === "reversed" ? "" : "+"}${fmtPoints(h.points)}`, h.status !== "earned"));
    }
  }

  $("[data-refer-copy]")?.addEventListener("click", (e) => {
    const input = $("[data-refer-url]");
    if (!input) return;
    copyWithFeedback(e.currentTarget, input.value, () => { input.focus(); input.select(); });
  });

  let currentSpendable = 0;

  async function load() {
    show("loading");
    const { status, data } = await api("/me");
    if (status === 401) {
      // Signed, but the session Shopify knows about is gone: ask for a fresh sign-in instead of showing an outage.
      $(".pw-rewards__text", states["logged-out"]).textContent = "Your session has expired. Please sign in again to see your points.";
      show("logged-out");
      return;
    }
    if (status !== 200 || !data || !data.ok) { show("unavailable"); return; }
    if (!data.loggedIn) {
      renderEarn($("[data-earn]", states["logged-out"]), data.earning);
      renderRewards($("[data-rewards]", states["logged-out"]), data.rewards, "public");
      if (themeSaysLoggedIn) $(".pw-rewards__text", states["logged-out"]).textContent = "Your session has expired. Please sign in again to see your points.";
      show("logged-out");
      return;
    }
    currentSpendable = data.points.spendable;
    renderBalance($("[data-balance]", states.member), data);
    renderEarn($("[data-earn]", states.member), data.earning);
    renderRewards($("[data-rewards]", states.member), data.rewards, "member");
    renderReferral($("[data-referral]", states.member), data.referral);
    renderRedemptions($("[data-redemptions]", states.member), $("[data-redemptions-empty]", states.member), data.redemptions || []);
    renderHistory($("[data-history]", states.member), $("[data-history-empty]", states.member), data.history || []);
    show("member");
  }

  // ── redeem dialog ──
  const dialog = $("[data-dialog]");
  const confirmView = $("[data-dialog-confirm]", dialog);
  const resultView = $("[data-dialog-result]", dialog);
  const errorEl = $("[data-dialog-error]", dialog);
  const redeemBtn = $("[data-dialog-redeem]", dialog);
  const redeemLabel = redeemBtn ? redeemBtn.textContent : "Redeem";
  const redeemBusyLabel = (redeemBtn && redeemBtn.dataset.busyLabel) || "Redeeming…";
  let pendingReward = null;
  let requestId = "";

  function setBusy(busy) {
    redeemBtn.disabled = busy;
    redeemBtn.setAttribute("aria-busy", busy ? "true" : "false");
    redeemBtn.textContent = busy ? redeemBusyLabel : redeemLabel;
    dialog.classList.toggle("is-busy", busy);
  }

  function openRedeem(reward) {
    if (!dialog || typeof dialog.showModal !== "function") { window.location.href = loginUrl; return; }
    pendingReward = reward;
    requestId = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    $("[data-dialog-title]", dialog).textContent = `${valueLabel(reward)} for ${fmtPoints(reward.pointsCost)} points`;
    $("[data-dialog-text]", dialog).textContent = `You have ${fmtPoints(currentSpendable)} points to spend. Redeeming gives you a single-use code for your account${reward.minSubtotal ? ` on orders over ${fmtRand(reward.minSubtotal)}` : ""}${reward.expiryDays ? `, valid for ${reward.expiryDays} days` : ""}. It cannot be combined with another discount.`;
    errorEl.hidden = true;
    setBusy(false);
    confirmView.hidden = false;
    resultView.hidden = true;
    dialog.showModal();
  }

  redeemBtn?.addEventListener("click", async () => {
    if (!pendingReward || redeemBtn.disabled) return;
    setBusy(true);
    errorEl.hidden = true;
    const { status, data } = await api("/redeem", { method: "POST", body: { reward_id: pendingReward.id, request_id: requestId }, timeoutMs: REDEEM_TIMEOUT_MS });
    if (status === 200 && data && data.redemption) {
      const r = data.redemption;
      $("[data-result-title]", dialog).textContent = r.code ? `${r.valueLabel} is yours.` : "Your points are reserved.";
      $("[data-result-code]", dialog).textContent = r.code || "";
      $("[data-result-code]", dialog).hidden = !r.code;
      $("[data-result-text]", dialog).textContent = r.code
        ? `Enter ${r.code} at checkout, or apply it now. Single use, for your account only.`
        : "We are finalising your code. It will appear under “Your reward codes” shortly — no need to redeem again.";
      const apply = $("[data-result-apply]", dialog);
      apply.hidden = !r.code;
      if (r.code) apply.href = applyUrl(r.code);
      $("[data-result-copy]", dialog).hidden = !r.code;
      setBusy(false);
      confirmView.hidden = true;
      resultView.hidden = false;
      load();
      return;
    }
    if (status === 401) { window.location.href = loginUrl; return; }
    const messages = {
      insufficient_points: "Not enough spendable points yet.", inactive_reward: "That reward is no longer available.", unknown_reward: "That reward is no longer available.",
      in_progress: "A redemption is already being processed. Give it a moment.", limit_reached: "That reward has reached its limit.", rate_limited: "Too many attempts. Please wait a minute.",
      failed: "The reward could not be issued. Your points have been returned.", member_not_active: "This rewards account is not active. Please contact us.",
      rewards_unavailable: "Rewards are taking a moment. Please try again shortly.",
    };
    const key = data && (data.action || data.error);
    errorEl.textContent = messages[key] || (status === 0 ? "That took too long. If points were deducted, your code will appear under “Your reward codes” — please check before trying again." : "Rewards are taking a moment. Please try again shortly.");
    errorEl.hidden = false;
    setBusy(false);
    if (key === "in_progress" || status === 0) { redeemBtn.disabled = true; load(); }
  });

  $("[data-result-copy]", dialog)?.addEventListener("click", (e) => copyWithFeedback(e.currentTarget, $("[data-result-code]", dialog).textContent));

  load();
}
